import api from './api'

// Agent interfaces
export type AgentCapabilities = {
  has_internet: boolean
  supports_ceph: boolean
  supports_zfs: boolean
  cpu_cores: number
  memory_bytes: number
  storage_pools: string[]
  network_bridges: string[]
}

export type AgentConfig = {
  metrics_interval: number
  vm_metrics_interval: number
  storage_metrics_interval: number
  heartbeat_interval: number
  max_concurrent_requests: number
}

export type Agent = {
  id: string
  cluster_id: string
  node_name: string
  status: string
  last_seen_at: string | null
  last_heartbeat_at: string | null
  agent_version: string | null
  proxmox_version: string | null
  connected_at: string | null
  disconnected_at: string | null
  connection_count: number
  capabilities: AgentCapabilities | null
  config: AgentConfig | null
  last_metrics: Record<string, any> | null
  last_metrics_at: string | null
  last_error: string | null
  last_error_at: string | null
  error_count: number
  created_at: string
  updated_at: string
  is_online: boolean
}

export type AgentListResponse = {
  items: Agent[]
  total: number
  connected: number
}

export type RegistrationToken = {
  id: string
  cluster_id: string
  token: string
  max_uses: number
  used_count: number
  remaining_uses: number
  expires_at: string
  is_active: boolean
  is_valid: boolean
  description: string | null
  created_at: string
  last_used_at: string | null
}

export type TokenListResponse = {
  items: RegistrationToken[]
  total: number
}

export type CreateTokenRequest = {
  max_uses?: number
  expires_in_hours?: number
  description?: string
}

export type UpdateAgentStatusRequest = {
  status: 'maintenance' | 'pending'
}

export type UpdateAgentConfigRequest = {
  config: AgentConfig
}

// Node metrics
export type NodeMetrics = {
  cpu_usage: number
  cpu_count: number
  load_avg_1m: number
  load_avg_5m: number
  load_avg_15m: number
  memory_total: number
  memory_used: number
  memory_available: number
  memory_percent: number
  swap_total: number
  swap_used: number
  swap_percent: number
  disk_total: number
  disk_used: number
  disk_free: number
  disk_percent: number
  network_bytes_sent: number
  network_bytes_recv: number
  uptime_seconds: number
  temperature: number
}

export type VMMetrics = {
  vmid: number
  name: string
  status: string
  vm_type: string
  cpu_usage: number
  memory_used: number
  memory_max: number
  disk_read: number
  disk_write: number
  network_in: number
  network_out: number
  uptime: number
}

export type StoragePoolMetrics = {
  name: string
  type: string
  total_bytes: number
  used_bytes: number
  available_bytes: number
  percent_used: number
  active: boolean
}

export type AgentMetrics = {
  agent_id: string
  cluster_id: string
  node_id: string
  timestamp: number
  stored_at: string | null
  node: NodeMetrics | null
  vms: VMMetrics[]
  storage: StoragePoolMetrics[]
}

export type ClusterMetricsSummary = {
  cluster_id: string
  cluster_name: string
  updated_at: string | null
  node_count: number
  vm_count: number
  running_vms: number
  stopped_vms: number
  avg_cpu_usage: number
  total_memory_used: number
  total_memory_total: number
  memory_usage_percent: number
  total_disk_used: number
  total_disk_total: number
  disk_usage_percent: number
  storage_pools: Record<string, any>[]
}

export type MetricsHistoryResponse = {
  agent_id: string
  history: AgentMetrics[]
  count: number
}

// Agent package download
export type AgentPackageInfo = {
  version: string
  filename: string
  size_bytes: number
  size_human: string
  sha256: string | null
  download_url: string
}

// Agent update types
export type AgentVersionInfo = {
  agent_id: string
  node_name: string
  current_version: string | null
  latest_version: string
  is_outdated: boolean
  is_online: boolean
}

export type ClusterVersionSummary = {
  cluster_id: string
  cluster_name: string
  latest_version: string
  total_agents: number
  online_agents: number
  up_to_date: number
  outdated: number
  unknown_version: number
  agents: AgentVersionInfo[]
}

export type UpdateStatusResponse = {
  success: boolean
  message: string
  agent_id: string
  current_version: string | null
  target_version: string | null
  download_url: string | null
  /** True só quando o comando saiu para o agent (agent já na versão não dispara). */
  dispatched?: boolean
}

/** Por que o "Atualizar Todos" deixou um agent de fora (#1085) — conjunto fechado do backend. */
export type BulkUpdateNotTriggeredReason =
  | 'offline'
  | 'maintenance'
  | 'up_to_date'
  | 'unknown_version'
  | 'manual_update_required'
  | 'dispatch_failed'

export interface BulkUpdateTriggeredAgent {
  agent_id: string
  node_name: string
  current_version: string | null
  target_version: string | null
}

export interface BulkUpdateNotTriggeredAgent {
  agent_id: string
  node_name: string
  current_version: string | null
  reason: BulkUpdateNotTriggeredReason
  /** Causa do backend quando `reason` é `dispatch_failed`. */
  detail: string | null
}

export type BulkUpdateResponse = {
  success: boolean
  message: string
  total_agents: number
  outdated_agents: number
  updates_triggered: number
  agents: UpdateStatusResponse[]
  /** Os agents disparados — o que o modal acompanha (#1085). */
  triggered: BulkUpdateTriggeredAgent[]
  /** Os que não foram disparados, com o motivo (#1085). */
  not_triggered: BulkUpdateNotTriggeredAgent[]
}

export type TriggerUpdateRequest = {
  force?: boolean
}

export type AgentUpdateProgress = {
  agent_id: string
  node_name: string
  update_status: string // none, checking, downloading, extracting, installing, restarting, completed, failed, up_to_date
  update_progress: number // 0-100
  update_current_step: string | null
  update_error: string | null
  update_target_version: string | null
  update_started_at: string | null
  update_completed_at: string | null
  current_version: string | null
  is_updating: boolean
}

/** Progresso de todos os agents do cluster numa leitura só (#1085). */
export interface ClusterUpdateProgress {
  cluster_id: string
  /** Relógio do servidor — a régua de "sem resposta" mede contra ele, não contra o navegador. */
  server_time: string
  agents: AgentUpdateProgress[]
}

// Agents API functions
export const agentsApi = {
  // List agents for a cluster
  listAgents: async (clusterId: string, statusFilter?: string): Promise<AgentListResponse> => {
    const params = statusFilter ? `?status_filter=${statusFilter}` : ''
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents${params}`)
    return response.data
  },

  // Get agent details
  getAgent: async (clusterId: string, agentId: string): Promise<Agent> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/${agentId}`)
    return response.data
  },

  // Delete agent
  deleteAgent: async (clusterId: string, agentId: string): Promise<void> => {
    await api.delete(`/api/v1/agents/clusters/${clusterId}/agents/${agentId}`)
  },

  // Update agent status
  updateAgentStatus: async (
    clusterId: string,
    agentId: string,
    data: UpdateAgentStatusRequest,
  ): Promise<Agent> => {
    const response = await api.patch(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/status`,
      data,
    )
    return response.data
  },

  // Update agent config
  updateAgentConfig: async (
    clusterId: string,
    agentId: string,
    data: UpdateAgentConfigRequest,
  ): Promise<Agent> => {
    const response = await api.put(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/config`,
      data,
    )
    return response.data
  },

  // Get connected agents
  getConnectedAgents: async (clusterId: string): Promise<Record<string, any>[]> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/connected`)
    return response.data
  },

  // Token management
  createToken: async (clusterId: string, data: CreateTokenRequest): Promise<RegistrationToken> => {
    const response = await api.post(`/api/v1/agents/clusters/${clusterId}/agents/token`, data)
    return response.data
  },

  listTokens: async (clusterId: string, activeOnly?: boolean): Promise<TokenListResponse> => {
    const params = activeOnly ? '?active_only=true' : ''
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/tokens${params}`)
    return response.data
  },

  getToken: async (clusterId: string, tokenId: string): Promise<RegistrationToken> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/tokens/${tokenId}`)
    return response.data
  },

  revokeToken: async (clusterId: string, tokenId: string): Promise<void> => {
    await api.delete(`/api/v1/agents/clusters/${clusterId}/agents/tokens/${tokenId}`)
  },

  // Metrics
  getAgentMetrics: async (clusterId: string, agentId: string): Promise<AgentMetrics> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/${agentId}/metrics`)
    return response.data
  },

  getAgentMetricsHistory: async (
    clusterId: string,
    agentId: string,
    limit?: number,
  ): Promise<MetricsHistoryResponse> => {
    const params = limit ? `?limit=${limit}` : ''
    const response = await api.get(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/metrics/history${params}`,
    )
    return response.data
  },

  getAgentVMMetrics: async (
    clusterId: string,
    agentId: string,
    vmid?: number,
  ): Promise<VMMetrics[]> => {
    const params = vmid ? `?vmid=${vmid}` : ''
    const response = await api.get(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/metrics/vms${params}`,
    )
    return response.data
  },

  getAgentStorageMetrics: async (
    clusterId: string,
    agentId: string,
  ): Promise<StoragePoolMetrics[]> => {
    const response = await api.get(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/metrics/storage`,
    )
    return response.data
  },

  getClusterMetrics: async (clusterId: string): Promise<ClusterMetricsSummary> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/metrics`)
    return response.data
  },

  // Package download
  getPackageInfo: async (): Promise<AgentPackageInfo> => {
    const response = await api.get('/api/v1/agents/download/info')
    return response.data
  },

  downloadPackage: async (): Promise<void> => {
    const response = await api.get('/api/v1/agents/download', {
      responseType: 'blob',
    })

    // Get filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = 'nebula-agent.tar.gz'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/)
      if (match) {
        filename = match[1]
      }
    }

    // Create download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  // Agent version and update endpoints
  getClusterVersionSummary: async (clusterId: string): Promise<ClusterVersionSummary> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/versions`)
    return response.data
  },

  triggerAgentUpdate: async (
    clusterId: string,
    agentId: string,
    force: boolean = false,
  ): Promise<UpdateStatusResponse> => {
    const response = await api.post(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/update`,
      { force },
    )
    return response.data
  },

  triggerBulkUpdate: async (
    clusterId: string,
    force: boolean = false,
  ): Promise<BulkUpdateResponse> => {
    const response = await api.post(`/api/v1/agents/clusters/${clusterId}/agents/update-all`, {
      force,
    })
    return response.data
  },

  // Get agent update progress
  getUpdateProgress: async (clusterId: string, agentId: string): Promise<AgentUpdateProgress> => {
    const response = await api.get(
      `/api/v1/agents/clusters/${clusterId}/agents/${agentId}/update-progress`,
    )
    return response.data
  },

  // Progresso de atualização de todos os agents do cluster (#1085)
  getClusterUpdateProgress: async (clusterId: string): Promise<ClusterUpdateProgress> => {
    const response = await api.get(`/api/v1/agents/clusters/${clusterId}/agents/update-progress`)
    return response.data
  },

  // Clear agent update status
  clearUpdateStatus: async (clusterId: string, agentId: string): Promise<void> => {
    await api.post(`/api/v1/agents/clusters/${clusterId}/agents/${agentId}/clear-update-status`)
  },
}

export default agentsApi
